"use client";

import { useState, Suspense } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/sections/Footer";
import { getLangFromPath } from "@/lib/request";
import common from "@/lib/common";
import { processImageUrl } from "@/lib/imageUrl";
import Link from "next/link";
import { useSearchParams, useRouter, useParams } from "next/navigation";
import { useSiteData } from "@/contexts/SiteDataContext";
import Image from "next/image";

interface NewsItem {
  id: string | number;
  face: string;
  date: string;
  title: string;
  desc: string;
  createtime: number;
  keyword?: string;
  uniq?: string;
}

interface NewsDetail {
  content: string;
  id: string | number;
  face: string;
  date: string;
  title: string;
  desc: string;
  createtime: number;
  keyword?: string;
  uniq?: string;
}

interface CallItem {
  name: string;
  title: string;
  url?: string;
}

interface ClientPageProps {
  newsList: NewsItem[];
  newsDetail: NewsDetail | null;
}

function NewsContent({ newsList, newsDetail }: ClientPageProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { settingData, menuData } = useSiteData();
  const [currentLang] = useState<string>(getLangFromPath());
  const [searchKeyword, setSearchKeyword] = useState(
    searchParams?.get("keyword") || "",
  );

  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");

  const isValidMenuData = Array.isArray(menuData);
  const cate = isValidMenuData
    ? menuData.find((item) => item.title == "News")?.children || []
    : [];
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME || "yy";

  const getNewsUrl = (item: NewsItem | NewsDetail | undefined | null): string => {
    if (!item) return "#";
    if (item.uniq) return `/${currentLang}/news/${item.uniq}.html`;
    if (item.keyword) return `/${currentLang}/news/${sanitizeKeyword(item.keyword)}.html`;
    return `/${currentLang}/news/${item.id}.html`;
  };

  const id = (() => {
    const qid = searchParams?.get("id");
    if (qid) return qid;
    if (newsDetail?.id) return String(newsDetail.id);
    if (typeof window === "undefined") return "";
    const path = window.location.pathname;
    const segments = path.split("/").filter(Boolean);
    const last = segments[segments.length - 1] || "";
    const raw = last.replace(/\.html?$/i, "");
    const match = raw.match(/-([0-9]+)$/);
    return match ? match[1] : "";
  })();

  // 搜索：设置 URL 查询参数，服务端重新过滤
  const handleSearch = () => {
    const keyword = searchKeyword.trim();
    const currentPath =
      typeof window !== "undefined" ? window.location.pathname : "";
    const baseParams = new URLSearchParams();
    if (keyword) baseParams.set("keyword", keyword);
    if (id) baseParams.set("id", id);
    const query = baseParams.toString();
    router.push(query ? `${currentPath}?${query}` : currentPath);
  };

  const handleSendClick = async () => {
    if (!email) return setSubmitMessage("请输入邮箱");
    if (!message) return setSubmitMessage("请输入内容");
    setSubmitLoading(true);
    try {
      const { request } = await import("@/lib/request");
      await request.post("/api/needs", { email, message, lang: currentLang });
      setSubmitMessage("发送成功！");
      setEmail("");
      setMessage("");
    } catch {
      setSubmitMessage("发送失败");
    } finally {
      setSubmitLoading(false);
    }
  };

  const defaultDetail = {
    id: id || 1,
    face: "",
    date: "",
    title: "Please select a news item",
    desc: "Click on any news title from the left to view details",
    createtime: Date.now() / 1000,
    content: "",
  };
  const display = newsDetail || defaultDetail;

  // 创建安全的URL友好字符串
  const sanitizeKeyword = (keyword: string): string => {
    // 1. 安全处理空值
    const input = (keyword || "").toString().trim();

    // 3. SEO/GEO 标准清洗规则（全球通用最友好）
    return input
      .toLowerCase() // 全部小写（SEO强制标准）
      .replace(/\s+/g, "-") // 空格 → 短横线
      .replace(/[^a-z0-9\-]/g, "") // 只保留 英文+数字+横线（SEO/GEO满分）
      .replace(/-+/g, "-") // 多条横线 → 1条
      .replace(/^-+|-+$/g, ""); // 去掉首尾横线
  };

  return (
    <>
      <div className="page flex-col justify-end">
        <Navbar isVisible={true} />
        <div>
          <div className="news-background"></div>
          <div
            className="section-container newb"
            style={{ margin: "50px auto 100px auto" }}
          >
            <div className="width100">
              <span
                className="block"
                style={{ textAlign: "center", margin: "50px auto" }}
              >
                <Link href={`/${currentLang}`}>Home</Link>&nbsp;&gt;&nbsp;{" "}
                <Link href={`/${currentLang}/news`}>News</Link>&nbsp;&gt;&nbsp;{" "}
                <Link
                  href={getNewsUrl(newsDetail)}
                >
                  {newsDetail?.title}
                </Link>
              </span>
            </div>

            <div className="width30 float" id="nleft">
              <div className="box_2 flex-col">
                <span className="text_1">Search</span>
                {Array.isArray(cate) &&
                  cate.length > 0 &&
                  cate.map((item: CallItem, index: number) => {
                    return (
                      <div className=" width60 float" key={index}>
                        <span>
                          <a href={item.url}>{item.title}</a>
                        </span>
                      </div>
                    );
                  })}
                <div className="box_3 flex-row justify-between relative">
                  <input
                    className="input_1 width100"
                    style={{ border: "none", background: "rgb(242, 242, 242)" }}
                    type="text"
                    placeholder="Enter Your Keyword..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <svg
                    style={{
                      right: "20px",
                      top: "12px",
                      position: "absolute",
                      cursor: "pointer",
                    }}
                    fill="#000"
                    viewBox="0 0 1024 1024"
                    width="20"
                    height="20"
                    onClick={handleSearch}
                  >
                    <path d="M1005.109936 914.29266l-250.119943-250.37582a417.591556 417.591556 0 0 0-632.592355-541.436111 417.719495 417.719495 0 0 0 295.410203 713.065729 414.393091 414.393091 0 0 0 246.345754-80.79322l250.119942 250.37582a64.225172 64.225172 0 0 0 90.900368-90.836398zM203.190857 632.827763a303.662242 303.662242 0 1 1 214.93683 89.109227A304.301935 304.301935 0 0 1 203.190857 632.827763z" />
                  </svg>
                </div>
              </div>

              <div className="box_4">
                <h2 className="text_11">News</h2>
                <div className="group_1 overflow">
                  {newsList &&
                    newsList.length >= 1 &&
                    newsList.map((item) => (
                      <div
                        key={item.id}
                        className={`image-wrapper_1 ${item.id == id ? "active" : ""}`}
                      >
                        <a href={getNewsUrl(item)}>
                          <Image
                            className="image_2 float display"
                            src={processImageUrl(item.face) || ""}
                            width={121}
                            height={74}
                            alt={item.title}
                            loading="lazy"
                          />
                        </a>
                        <div className="text-group_1 left overflow">
                          <span className="text_4 width100 float">
                            <a href={getNewsUrl(item)}>
                              {common.formatTimestamp(item.createtime)}
                            </a>
                          </span>
                          <span className="text_5 width100 float">
                            <a href={getNewsUrl(item)}>
                              {item.title}
                            </a>
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              <div className="box_5 flex-col">
                <span className="text_12">Contact</span>
                <div className="text-wrapper_2 flex-col">
                  <input
                    className="email width100 input_1"
                    placeholder="Your E-mail"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="text-wrapper_3 flex-col">
                  <input
                    className="message width100 input_1"
                    placeholder="Your Content"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>
                {submitMessage && (
                  <div
                    style={{
                      padding: "10px",
                      color: submitMessage.includes("成功") ? "green" : "red",
                      textAlign: "center",
                    }}
                  >
                    {submitMessage}
                  </div>
                )}
                <div
                  className="text-wrapper_4 flex-col"
                  onClick={handleSendClick}
                >
                  <span
                    className="fff"
                    style={{
                      cursor: submitLoading ? "not-allowed" : "pointer",
                      opacity: submitLoading ? 0.7 : 1,
                    }}
                  >
                    {submitLoading ? "Sending..." : "Send"}
                  </span>
                </div>
              </div>
            </div>

            <div className="width70 right">
              <div
                className="news-detail"
                style={{ width: "95%", marginLeft: "5%" }}
              >
                <h1 className="news-title">{newsDetail?.title}</h1>
                <span className="news-date">
                  {newsDetail?.createtime
                    ? new Date(newsDetail.createtime * 1000).toLocaleDateString(
                        "en-GB",
                        { day: "2-digit", month: "short", year: "numeric" },
                      )
                    : "03 Oct 2025"}
                </span>
                <div className="news-content">
                  <p
                    className="news-desc"
                    dangerouslySetInnerHTML={{
                      __html: newsDetail?.content || newsDetail?.desc || "",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; }
        .input_1 {
  transition: all 0.3s ease;
  border: none;
  height:45px;
  background: rgb(242, 242, 242);
 text-indent: 10px;
}
  .news-background {
          position: relative;
          top: 0;
          left: 0;
          width: 100%;
          height: 100px;
          background-size:100% auto;
          background-position:center;
          background-image:url(${processImageUrl("https://oss.yy-aicloud.com/uploads/20260422/69e85c99c5b900af854284f4ab0cfea8fcfd889cbb41a.jpg?x-oss-process=image/resize,m_fixed,w_1920")});
          z-index: 0;
          background-repeat: no-repeat;
           animation: zoomIn 20s ease-in-out infinite alternate;
        }
           @keyframes zoomIn {
          0% {
            background-size:100% auto;
          }
          100% {
            background-size:160% auto;
          }
        }
        body { margin: 0; padding: 0; }
        .page { background-color: rgba(255, 255, 255, 1); width: 100%; overflow: hidden; }
        .width30 { width: 30%; flex-shrink: 0; }
        .width70 { width: 70%; }
        .box_2 { width: 100%; border: 1px solid rgba(190, 190, 190, 1); display: flex; flex-direction: column; justify-content: center; padding: 26px; }
        .text_1 { font-size: 24px; font-family: Montserrat-Regular; color: rgba(0, 0, 0, 1); margin-bottom: 10px; }
        .box_3 {  height: 50px; display: flex; flex-direction: row; justify-content: space-between; align-items: center; }
        .box_4 { width: 100%; border: 1px solid rgba(190, 190, 190, 1); margin-top: 32px; padding: 20px; }
        .text_11 { font-size: 24px; font-family: Montserrat-Medium; color: rgba(0, 0, 0, 1); margin-bottom: 20px; display: block; }
        .group_1 { width: 90%; margin: 14px 0 37px 0px; }
        .image-wrapper_1 { width: 100%; float: left; margin-top: 20px; display: flex; align-items: flex-start; opacity: 0.7; transition: opacity 0.3s; }
        .image-wrapper_1.active { opacity: 1; background: #f5f5f5; }
        .image-wrapper_1:hover { opacity: 1; }
        .image_2 { width: 121px; height: 74px; object-fit: cover; }
        .text-group_1 { width: 60%; margin: 5px 0 0 20px; }
        .text_4 { width: 100%; float: left; font-size: 14px; font-family: Montserrat-Regular; color: rgba(153, 199, 55, 1); margin-bottom: 5px; }
        .text_5 { width: 100%; float: left; height:40px; overflow:hidden; font-size: 14px; font-family: Montserrat-Light; color: rgba(18, 12, 11, 1); line-height: 1.4; }
        .box_5 { width: 100%; border: 1px solid rgba(190, 190, 190, 1); margin-top: 32px; padding: 26px; }
        .text_12 { font-size: 24px; font-family: Montserrat-Regular; color: rgba(0, 0, 0, 1); margin-bottom: 24px; display: block; }
        .text-wrapper_2, .text-wrapper_3 { height: 42px;  display: flex; align-items: center; margin-bottom: 24px; }
        .text-wrapper_4 { background-image: linear-gradient(90deg, rgba(153, 199, 55, 1) 0, rgba(76, 181, 222, 1) 100%); border-radius: 118px; height: 44px; display: flex; align-items: center; justify-content: center; cursor: pointer; width:200px; margin:auto; }
        .text_15 { font-size: 16px; font-family: Montserrat-Regular; color: rgba(248, 248, 248, 1); }
        .news-detail { display: flex; flex-direction: column; }
        .news-date { background-image: linear-gradient(360deg, rgba(153, 199, 55, 1) 0, rgba(76, 181, 222, 1) 100%); font-size: 16px; font-family: Montserrat-Medium; -webkit-background-clip: text; -webkit-text-fill-color: transparent; display: inline-block; margin-bottom: 15px; }
        .news-title { font-size: 24px; font-family: Montserrat-Regular; color: rgba(0, 0, 0, 1); line-height: 1.4; margin-bottom: 15px; font-weight: normal; }
        .news-desc { font-size: 16px; font-family: Montserrat-Light; color: rgba(0, 0, 0, 0.8); line-height: 1.8; margin-bottom: 25px; }
        
        /* ========== 平板适配 (768px - 1024px) ========== */
        @media screen and (max-width: 1024px) {
          .section-container {
            width: 95% !important;
            margin: 100px auto 50px auto !important;
          }
          .newb{
                 margin-top:9rem !important;
                }
          .width30 {
            width: 35% !important;
          }
          
          .width70 {
            width: 65% !important;
          }
          
          .news-detail {
            width: 100% !important;
            margin-left: 0 !important;
          }
          
          .text_1, .text_11, .text_12 {
            font-size: 20px !important;
          }
          
          .box_2, .box_4, .box_5 {
            padding: 20px !important;
          }
          
          .image_2 {
            width: 100px !important;
            height: 60px !important;
          }
          
          .text-group_1 {
            width: 65% !important;
            margin: 5px 0 0 15px !important;
          }
          
          .text_5 {
            height: 35px !important;
            font-size: 13px !important;
          }
          
          .news-title {
            font-size: 20px !important;
          }
          
          .news-desc {
            font-size: 14px !important;
          }
        }
        
        /* ========== 手机适配 (767px 以下) ========== */
        @media screen and (max-width: 767px) {
          .section-container {
            width: 95% !important;
            margin: 50px auto 30px auto !important;
          }
          #nleft .group_4,#nleft .box_5,#nleft .box_4 {
                display:none;
              }
                .newb{
                 margin-top:9rem !important;
                }
               
          .width30 {
            width: 100% !important;
            float: none !important;
            margin-bottom: 30px !important;
          }
          
          .width70 {
            width: 100% !important;
            float: none !important;
          }
          
          .news-detail {
            width: 100% !important;
            margin-left: 0 !important;
          }
          
          .box_2 {
            height: 120px !important;
            padding: 15px !important;
          }
          
          .text_1, .text_11, .text_12 {
            font-size: 18px !important;
            margin-bottom: 10px !important;
          }
          
          .box_3 {
            height: 45px !important;
          }
          
          .box_4, .box_5 {
            padding: 15px !important;
            margin-top: 20px !important;
          }
          
          .image-wrapper_1 {
            margin-top: 15px !important;
          }
          
          .image_2 {
            width: 80px !important;
            height: 50px !important;
          }
          
          .text-group_1 {
            width: 70% !important;
            margin: 5px 0 0 10px !important;
          }
          
          .text_4 {
            font-size: 12px !important;
          }
          
          .text_5 {
            height: 30px !important;
            font-size: 12px !important;
          }
          
          .text-wrapper_2, .text-wrapper_3 {
            height: 38px !important;
            margin-bottom: 15px !important;
          }
          
          .text-wrapper_4 {
            width: 160px !important;
            height: 40px !important;
          }
          
          .news-date {
            font-size: 14px !important;
            margin-bottom: 10px !important;
          }
          
          .news-title {
            font-size: 18px !important;
            margin-bottom: 10px !important;
          }
          
          .news-desc {
            font-size: 14px !important;
            line-height: 1.6 !important;
            margin-bottom: 20px !important;
          }
        }
      `}</style>
    </>
  );
}

export default function ClientPage({ newsList, newsDetail }: ClientPageProps) {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <NewsContent newsList={newsList} newsDetail={newsDetail} />
    </Suspense>
  );
}